import { el } from '@/ui/dom';
import { cssColor, ordinal } from '@/ui/format';
import { panelHead } from '@/ui/social/clubUi';
import type { ChampionshipRow } from '@/core/Progression';

/**
 * tourProgress.ts — M158c (owner: "at the end of each tour race show a proper
 * tour progress screen: the current tour ranking and how many races are left,
 * online and offline"). ONE renderer for both tours, so the two can never
 * drift: the step after a tour race's own results.
 *
 *   ── TOUR STANDINGS ──   RACE 3 OF 9 COMPLETE · 6 RACES LEFT
 *   the route: a stop per race (done = your place medal, next = pulsing)
 *   the position line: "YOU'RE 2ND OVERALL · 4 PTS BEHIND NOVA"
 *   the table: rank, ▲/▼ movement since the last race, +points this race
 */
export interface TourStop {
  /** City name; null for an online stop the room hasn't revealed yet. */
  name: string | null;
  /** Your finish in that race (1-based), null if not raced yet. */
  place: number | null;
}

export interface TourProgressData {
  /** The race just finished (1-based). */
  raceNum: number;
  tourLen: number;
  stops: TourStop[];
  /** The running championship AFTER this race, best first. */
  table: ChampionshipRow[];
  /** The championship BEFORE this race (movement arrows + points gained). */
  prevTable: ChampionshipRow[];
}

const MEDAL = ['gold', 'silver', 'bronze'];

/** The position line under the title — the one fact a racer wants first. */
export function tourPositionLine(table: ChampionshipRow[]): string {
  const i = table.findIndex((r) => r.isPlayer);
  if (i < 0) return '';
  const me = table[i];
  if (i === 0) {
    const next = table[1];
    const lead = next ? me.points - next.points : 0;
    return lead > 0 ? `YOU LEAD THE TOUR BY ${lead} PTS` : 'YOU SHARE THE TOUR LEAD';
  }
  const leader = table[0];
  const gap = leader.points - me.points;
  return gap > 0
    ? `YOU'RE ${ordinal(i + 1).toUpperCase()} OVERALL · ${gap} PTS BEHIND ${leader.name.toUpperCase()}`
    : `YOU'RE ${ordinal(i + 1).toUpperCase()} OVERALL · LEVEL ON POINTS`;
}

export function renderTourProgress(parent: HTMLElement, d: TourProgressData): void {
  const left = Math.max(0, d.tourLen - d.raceNum);
  const wrap = el('div', 'tgp-wrap', parent);

  // ---- head: kicker, title, how far along ----
  const head = el('div', 'tgp-head', wrap);
  el('div', 'rid-kicker tgp-kicker', head).textContent = 'WORLD TOUR CUP';
  el('div', 'tgp-title', head).textContent = 'TOUR STANDINGS';
  el('div', 'tgp-sub', head).textContent =
    left === 0
      ? `RACE ${d.raceNum} OF ${d.tourLen} · FINAL RACE DONE`
      : `RACE ${d.raceNum} OF ${d.tourLen} DONE · ${left} ${left === 1 ? 'RACE' : 'RACES'} LEFT`;
  el('div', 'tgp-pos', head).textContent = tourPositionLine(d.table);

  // ---- the route ----
  const route = el('div', 'tgp-route', wrap);
  route.style.setProperty('--stops', String(d.stops.length));
  const done = Math.min(d.raceNum, d.stops.length);
  const line = el('div', 'tgp-route-line', route);
  el('div', 'tgp-route-fill', line).style.width =
    d.stops.length > 1 ? `${((done - 1) / (d.stops.length - 1)) * 100}%` : '0%';
  d.stops.forEach((s, i) => {
    const state = i < done ? 'done' : i === done ? 'next' : 'later';
    const stop = el('div', `tgp-stop tgp-${state}`, route);
    stop.style.setProperty('--i', String(i));
    const pin = el('div', 'tgp-pin', stop);
    if (s.place !== null && s.place > 0) {
      pin.textContent = String(s.place);
      if (s.place <= 3) pin.classList.add(`tgp-${MEDAL[s.place - 1]}`);
    } else pin.textContent = String(i + 1);
    if (i === done - 1) stop.classList.add('tgp-just');
    el('div', 'tgp-stop-name', stop).textContent = s.name
      ? s.name.toUpperCase()
      : state === 'next'
        ? 'NEXT'
        : `STOP ${i + 1}`;
  });

  // ---- the standings, with movement since the last race ----
  panelHead(wrap, 'OVERALL RANKING', 'tgp-standhead');
  const prevRank = new Map<number, number>();
  const prevPts = new Map<number, number>();
  d.prevTable.forEach((r, i) => {
    prevRank.set(r.racerId, i + 1);
    prevPts.set(r.racerId, r.points);
  });
  const first = d.prevTable.length === 0;
  const list = el('div', 'tgp-list', wrap);
  d.table.forEach((row, i) => {
    const r = el('div', `tgp-row${row.isPlayer ? ' me' : ''}`, list);
    r.style.setProperty('--row-i', String(i));
    const rank = i + 1;
    el('span', `tgp-rank${rank <= 3 ? ` tgp-${MEDAL[rank - 1]}` : ''}`, r).textContent =
      String(rank);
    const move = el('span', 'tgp-move', r);
    const was = prevRank.get(row.racerId);
    if (!first && was !== undefined && was !== rank) {
      const up = was > rank;
      move.classList.add(up ? 'tgp-up' : 'tgp-down');
      move.textContent = `${up ? '▲' : '▼'}${Math.abs(was - rank)}`;
    } else if (!first) {
      move.classList.add('tgp-same');
      move.textContent = '–';
    }
    el('span', 'chip-dot', r).style.background = cssColor(row.color);
    el('span', 'tgp-name', r).textContent = row.name;
    const gained = row.points - (prevPts.get(row.racerId) ?? 0);
    el('span', 'tgp-gain', r).textContent = gained > 0 ? `+${gained}` : '';
    el('span', 'tgp-pts', r).textContent = `${row.points} PTS`;
  });
}
